import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { lumi } from "../lib/lumi";
import {Calendar, User, Tag, ArrowLeft, Clock, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon, X, Image as ImageIcon, ChevronRight, Instagram, MapPin} from 'lucide-react';
import DOMPurify from 'dompurify';

// 分類定義
const CATEGORIES = {
  news: { zh: "最新消息", en: "News" },
  guide: { zh: "沖煮指南", en: "Brewing Guide" },
  beanstory: { zh: "豆單故事", en: "Bean Story" },
  events: { zh: "活動紀錄", en: "Events" }
};

interface Article {
  _id: string;
  title: string;
  titleEn?: string;
  slug: string;
  excerpt?: string;
  content: string;
  category: "news" | "guide" | "beanstory" | "events";
  tags?: string[];
  featuredImage?: string;
  images?: string[];
  author?: string;
  publishedAt?: string;
  status: "draft" | "published" | "archived";
  isFeatured?: boolean;
  viewCount?: number;
  seoTitle?: string;
  seoDescription?: string;
  relatedProducts?: string[];
  relatedStores?: string[];
  createdAt: string;
  updatedAt?: string;
}

const ArticlePage: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [latestArticles, setLatestArticles] = useState<Article[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);

  // 從 URL 判斷語系
  const currentLang = window.location.pathname.startsWith("/en") ? "en" : "zh";

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug || !category) return;

      try {
        setLoading(true);
        const result = await lumi.entities.articles.list();
        
        // 兼容 Lumi SDK 的兩種返回格式
        const allArticles = Array.isArray(result) ? result : (result?.list || []);
        const publishedArticles = allArticles.filter((a: Article) => a.status === "published");

        if (publishedArticles.length > 0) {
          // 1. 找到當前文章
          const foundArticle = publishedArticles.find(
            (a: Article) => a.slug === slug && a.category === category
          );
          
          if (foundArticle) {
            setArticle(foundArticle);

            // 2. 相關文章（同分類，排除當前）
            const related = publishedArticles
              .filter((a: Article) => a.category === category && a.slug !== slug)
              .slice(0, 3);
            setRelatedArticles(related);

            // 3. 最新文章 (按時間排序)
            const sortedByDate = [...publishedArticles].sort((a, b) => {
              return new Date(b.publishedAt || b.createdAt).getTime() - new Date(a.publishedAt || a.createdAt).getTime();
            });
            setLatestArticles(sortedByDate.slice(0, 5));

            // 4. 精選文章
            const featured = publishedArticles.filter((a: Article) => a.isFeatured && a.slug !== slug).slice(0, 5);
            setFeaturedArticles(featured);

          } else {
            navigate(`/${currentLang}/lab`);
          }
        } else {
          navigate(`/${currentLang}/lab`);
        }
      } catch (error) {
        console.error("Failed to fetch article:", error);
        navigate(`/${currentLang}/lab`);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, category, navigate, currentLang]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return currentLang === "en"
      ? date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
      : date.toLocaleDateString("zh-TW", { year: "numeric", month: "2-digit", day: "2-digit" });
  };

  // 分享功能
  const shareUrl = `https://www.ongucoffee.com/${currentLang}/lab/${category}/${slug}`;
  const shareTitle = article ? (currentLang === "en" && article.titleEn ? article.titleEn : article.title) : "";

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    
    const shareLinks: { [key: string]: string } = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    };
    
    if (shareLinks[platform]) {
      window.open(shareLinks[platform], "_blank", "width=600,height=400");
    }
    setShowShareMenu(false);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setShowShareMenu(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // 圖片 Lightbox 功能
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    if (article?.images) {
      setCurrentImageIndex((prev) => (prev + 1) % article.images!.length);
    }
  };

  const prevImage = () => {
    if (article?.images) {
      setCurrentImageIndex((prev) => (prev - 1 + article.images!.length) % article.images!.length);
    }
  };

  // 鍵盤快捷鍵
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, article]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center pt-28">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  // SEO Meta 標籤
  const pageTitle = article.seoTitle || (currentLang === "en" && article.titleEn ? article.titleEn : article.title) + " - ONGU Studio Lab";
  const pageDescription = article.seoDescription || article.excerpt || article.content.substring(0, 160);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={"https://www.ongucoffee.com/" + currentLang + "/lab/" + category + "/" + slug} />
        {article.featuredImage && <meta property="og:image" content={article.featuredImage} />}
        <meta property="article:published_time" content={article.publishedAt || article.createdAt} />
        {article.updatedAt && <meta property="article:modified_time" content={article.updatedAt} />}
        {article.author && <meta property="article:author" content={article.author} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {article.featuredImage && <meta name="twitter:image" content={article.featuredImage} />}
        <link rel="canonical" href={"https://www.ongucoffee.com/" + currentLang + "/lab/" + category + "/" + slug} />
        <link rel="alternate" hreflang="zh" href={"https://www.ongucoffee.com/zh/lab/" + category + "/" + slug} />
        <link rel="alternate" hreflang="en" href={"https://www.ongucoffee.com/en/lab/" + category + "/" + slug} />
        <link rel="alternate" hreflang="x-default" href={"https://www.ongucoffee.com/zh/lab/" + category + "/" + slug} />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-gray-100 pt-16">


        {/* Hero 區域 - 全寬特色圖片 + 文章標題資訊 */}
        {article.featuredImage && (
          <div className="relative w-full h-[50vh] min-h-[400px] overflow-hidden bg-black">
            <img
              src={article.featuredImage}
              alt={currentLang === "en" && article.titleEn ? article.titleEn : article.title}
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
            
            {/* 文章標題與資訊 (Overlay) */}
            <div className="absolute inset-0 flex items-end justify-center">
              <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8 pb-12 lg:pb-16">
                <div className="max-w-4xl">
                  <div className="flex items-center space-x-3 mb-6">
                    <span className="text-xs font-light tracking-[0.2em] uppercase text-white/90 border border-white/30 px-4 py-1.5 backdrop-blur-sm">
                      {currentLang === "en" ? CATEGORIES[article.category as keyof typeof CATEGORIES].en : CATEGORIES[article.category as keyof typeof CATEGORIES].zh}
                    </span>
                    {article.isFeatured && (
                      <span className="text-xs font-light tracking-[0.2em] uppercase text-yellow-400 border border-yellow-400/60 px-4 py-1.5 backdrop-blur-sm">
                        ★ {currentLang === "en" ? "FEATURED" : "精選"}
                      </span>
                    )}
                  </div>

                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extralight tracking-wide text-white mb-6 leading-tight drop-shadow-md">
                    {currentLang === "en" && article.titleEn ? article.titleEn : article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-6 text-sm font-light text-white/80">
                    {article.author && (
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 opacity-80" />
                        <span>{article.author}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2 opacity-80" />
                      <span>{formatDate(article.publishedAt || article.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Article Content (8 cols) */}
            <div className="lg:col-span-8">
              {/* Breadcrumb (Moved here) */}
              <div className="mb-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-6 py-4 transition-colors duration-300">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Link
                    to={`/${currentLang}`}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 font-light tracking-wide"
                  >
                    {currentLang === "zh" ? "首頁" : "Home"}
                  </Link>
                  <ChevronRight size={14} className="text-gray-400 dark:text-gray-500" />
                  <Link
                    to={`/${currentLang}/lab`}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 font-light tracking-wide"
                  >
                    {currentLang === "zh" ? "醞玖製研中心" : "Lab"}
                  </Link>
                  <ChevronRight size={14} className="text-gray-400 dark:text-gray-500" />
                  <Link
                    to={`/${currentLang}/lab/${category}`}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 font-light tracking-wide"
                  >
                    {currentLang === "en" ? CATEGORIES[article.category as keyof typeof CATEGORIES].en : CATEGORIES[article.category as keyof typeof CATEGORIES].zh}
                  </Link>
                  <ChevronRight size={14} className="text-gray-400 dark:text-gray-500" />
                  <span className="text-gray-900 dark:text-gray-100 font-medium tracking-wide truncate">
                    {currentLang === "en" && article.titleEn ? article.titleEn : article.title}
                  </span>
                </div>
              </div>

              <article>
                {article.excerpt && (
                  <div className="mb-12 pl-6 border-l-2 border-gray-900 dark:border-gray-100">
                    <p className="text-xl font-light text-gray-700 dark:text-gray-300 leading-relaxed italic">
                      {article.excerpt}
                    </p>
                  </div>
                )}

                <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                  <div
                    className="article-content text-base sm:text-lg font-light leading-[1.9] text-gray-800 dark:text-gray-200"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
                  />
                </div>

                {/* Image Gallery */}
                {article.images && article.images.length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                      <ImageIcon className="w-5 h-5 text-gray-400 dark:text-gray-600" />
                      <h3 className="text-xl font-light tracking-wide text-gray-900 dark:text-gray-100">
                        {currentLang === "en" ? "Gallery" : "圖片畫廊"}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {article.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => openLightbox(index)}
                          className="group relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-900 rounded-lg"
                        >
                          <img
                            src={image}
                            alt={`Gallery ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA Buttons */}
                <div className="mb-12 flex flex-wrap gap-4">
                  <Link
                    to={`/${currentLang}/store/xinyi`}
                    className="flex-1 min-w-[200px] px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-center text-sm font-light tracking-[0.15em] uppercase border border-black dark:border-white hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition-all duration-300"
                  >
                    {currentLang === "en" ? "Visit Xinyi Store" : "去信義店喝咖啡"}
                  </Link>
                  <Link
                    to={`/${currentLang}/shop`}
                    className="flex-1 min-w-[200px] px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-center text-sm font-light tracking-[0.15em] uppercase border border-black dark:border-white hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition-all duration-300"
                  >
                    {currentLang === "en" ? "Shop Coffee Beans" : "網購咖啡豆"}
                  </Link>
                  <Link
                    to={`/${currentLang}/wholesale`}
                    className="flex-1 min-w-[200px] px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-center text-sm font-light tracking-[0.15em] uppercase border border-black dark:border-white hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition-all duration-300"
                  >
                    {currentLang === "en" ? "Wholesale Solutions" : "了解批發方案"}
                  </Link>
                </div>

                {/* Tags & Share */}
                <div className="pt-8 border-t border-gray-200 dark:border-gray-800">
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                      {article.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs font-light tracking-wider uppercase px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex gap-4">
                      <button onClick={() => handleShare('facebook')} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 transition-colors">
                        <Facebook size={18} />
                      </button>
                      <button onClick={() => handleShare('twitter')} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-400 transition-colors">
                        <Twitter size={18} />
                      </button>
                      <button onClick={() => handleShare('linkedin')} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-700 transition-colors">
                        <Linkedin size={18} />
                      </button>
                      <button onClick={handleCopyLink} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-600 transition-colors relative">
                        <LinkIcon size={18} />
                        {copySuccess && (
                          <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-xs bg-black text-white px-2 py-1 rounded">Copied</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            {/* Right Column: Sidebar (4 cols) */}
            <div className="lg:col-span-4 space-y-12">
              
              {/* 1. Store Card */}
              <Link 
                to={`/${currentLang}/store/xinyi`}
                className="block group relative aspect-square overflow-hidden rounded-lg shadow-lg"
              >
                <img 
                  src="https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg" 
                  alt="Visit Store" 
                  className="w-full h-full object-cover transition duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex flex-col items-center justify-center text-center p-6">
                  <MapPin className="w-8 h-8 text-white mb-4 opacity-80" />
                  <h3 className="text-2xl font-light text-white tracking-widest mb-2">
                    {currentLang === "en" ? "VISIT STORE" : "門市資訊"}
                  </h3>
                  <p className="text-white/80 text-sm font-light tracking-wide">
                    {currentLang === "en" ? "Experience ONGU Coffee" : "體驗 ONGU 現場氛圍"}
                  </p>
                  <span className="mt-6 px-6 py-2 border border-white/50 text-white text-xs tracking-[0.2em] hover:bg-white hover:text-black transition-all">
                    VIEW MORE
                  </span>
                </div>
              </Link>

              {/* 2. Latest Articles */}
              <div>
                <h3 className="text-sm font-medium tracking-[0.2em] text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-3 mb-6 uppercase">
                  {currentLang === "en" ? "Latest Stories" : "最新文章"}
                </h3>
                <div className="space-y-6">
                  {latestArticles.map((item) => (
                    <Link 
                      key={item._id} 
                      to={`/${currentLang}/lab/${item.category}/${item.slug}`}
                      className="flex gap-4 group"
                    >
                      <div className="w-20 h-20 flex-shrink-0 rounded bg-gray-200 dark:bg-gray-800 overflow-hidden">
                        {item.featuredImage && (
                          <img 
                            src={item.featuredImage} 
                            alt={item.title} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug mb-1 group-hover:text-gray-500 transition-colors line-clamp-2">
                          {currentLang === "en" && item.titleEn ? item.titleEn : item.title}
                        </h4>
                        <span className="text-xs text-gray-400 font-light">
                          {formatDate(item.publishedAt || item.createdAt)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* 3. Categories */}
              <div>
                <h3 className="text-sm font-medium tracking-[0.2em] text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-3 mb-6 uppercase">
                  {currentLang === "en" ? "Categories" : "文章分類"}
                </h3>
                <div className="flex flex-col space-y-2">
                  {Object.entries(CATEGORIES).map(([key, label]) => (
                    <Link
                      key={key}
                      to={`/${currentLang}/lab/${key}`}
                      className="flex items-center justify-between py-2 text-sm font-light text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:pl-2 transition-all"
                    >
                      <span>{currentLang === "en" ? label.en : label.zh}</span>
                      <ChevronRight size={14} className="opacity-0 group-hover:opacity-100" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* 4. Featured Articles */}
              {featuredArticles.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium tracking-[0.2em] text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-3 mb-6 uppercase">
                    {currentLang === "en" ? "Featured" : "精選推薦"}
                  </h3>
                  <div className="space-y-6">
                    {featuredArticles.map((item) => (
                      <Link 
                        key={item._id} 
                        to={`/${currentLang}/lab/${item.category}/${item.slug}`}
                        className="block group"
                      >
                        <div className="aspect-video rounded bg-gray-200 dark:bg-gray-800 overflow-hidden mb-3">
                          {item.featuredImage && (
                            <img 
                              src={item.featuredImage} 
                              alt={item.title} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          )}
                        </div>
                        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug group-hover:text-gray-500 transition-colors">
                          {currentLang === "en" && item.titleEn ? item.titleEn : item.title}
                        </h4>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Social Links */}
              <div>
                <h3 className="text-sm font-medium tracking-[0.2em] text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-3 mb-6 uppercase">
                  {currentLang === "en" ? "Follow Us" : "追蹤我們"}
                </h3>
                <div className="flex gap-4">
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <Instagram size={20} />
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <Facebook size={20} />
                  </a>
                  <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                    <Share2 size={20} />
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Lightbox 圖片查看器 */}
        {lightboxOpen && article?.images && (
          <div
            className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="absolute top-4 left-4 text-white text-sm font-light tracking-wide z-10">
              {currentImageIndex + 1} / {article.images.length}
            </div>

            <div
              className="relative max-w-7xl max-h-[90vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={article.images[currentImageIndex]}
                alt={`${currentLang === "en" && article.titleEn ? article.titleEn : article.title} - ${currentImageIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain"
              />
            </div>

            {article.images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black/50 hover:bg-black/70 p-4 backdrop-blur-sm"
                >
                  <ArrowLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black/50 hover:bg-black/70 p-4 backdrop-blur-sm rotate-180"
                >
                  <ArrowLeft className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
        )}

        {/* 相關文章 (Bottom Section) */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
              <h2 className="text-2xl font-light tracking-wide text-gray-900 dark:text-gray-100 mb-8 text-center">
                {currentLang === "en" ? "You May Also Like" : "猜你喜歡"}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedArticles.map((relatedArticle) => (
                  <Link
                    key={relatedArticle._id}
                    to={`/${currentLang}/lab/${relatedArticle.category}/${relatedArticle.slug}`}
                    onClick={() => window.scrollTo(0, 0)}
                    className="group block bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    {relatedArticle.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9 bg-gray-200 dark:bg-gray-800 overflow-hidden">
                        <img
                          src={relatedArticle.featuredImage}
                          alt={currentLang === "en" && relatedArticle.titleEn ? relatedArticle.titleEn : relatedArticle.title}
                          className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="text-base font-light tracking-wide text-gray-900 dark:text-gray-100 mb-2 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors line-clamp-2">
                        {currentLang === "en" && relatedArticle.titleEn ? relatedArticle.titleEn : relatedArticle.title}
                      </h3>
                      <p className="text-xs font-light text-gray-500 dark:text-gray-500">
                        {formatDate(relatedArticle.publishedAt || relatedArticle.createdAt)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default ArticlePage;
